import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Container, Typography, Button, Link } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import TwitterIcon from "@mui/icons-material/Twitter";

interface NavLink {
  label: string;
  id: string;
  path?: string;
}

interface LegalLink {
  label: string;
  path: string;
}

interface SocialLink {
  name: string;
  icon: React.ElementType;
  url: string;
}

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

const NAV_LINKS: NavLink[] = [
  { label: "Home", id: "hero", path: "https://extractfast.com/" },
  { label: "About Us", id: "about", path: "https://extractfast.com/#about" },
  { label: "Pricing", id: "pricing", path: "https://extractfast.com/#pricing" },
  { label: "Careers", id: "careers", path: "https://extractfast.com/careers" },
  { label: "Contact Us", id: "contact", path: "https://extractfast.com/#contact" },
];

const LEGAL_LINKS: LegalLink[] = [
  { label: "Privacy Policy", path: "https://extractfast.com/privacypolicy" },
  { label: "Terms of Service", path: "https://extractfast.com/termsofservice" },
];

  const socialLinks: SocialLink[] = [
    { name: "X", icon: TwitterIcon, url: "https://x.com/ExtractFast" },
    { name: "Linkedin", icon: LinkedInIcon, url: "https://www.linkedin.com/company/extractfast" },
    { name: "Mail", icon: EmailIcon, url: "mailto:website@extractfast.com" },
  ];

  const handleScroll = async (link: NavLink | string) => {
    const targetId = typeof link === "string" ? link : link.id;
    const targetPath = typeof link === "object" ? link.path : undefined;

    if (targetPath) {
  window.location.href = targetPath;
  return;
}

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(`#${targetId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return;
    }

    const element = document.querySelector(`#${targetId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        pt: 6,
        pb: 8,
        backgroundColor: "#1a2e44",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      <Container maxWidth="lg">
        {/* Upper Row: Logo + Nav Links + Legal Links */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            rowGap: 3,
            columnGap: 3,
          }}
        >
          {/* Logo Button */}
          <Button
            onClick={() => handleScroll("hero")}
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <Box
              component="img"
              alt="ExtractFast"
              src="/ppp.png"
              sx={{ height: 32, width: 32, objectFit: "contain" }}
            />
            <Typography
              component="span"
              sx={{ fontSize: "1.125rem", fontWeight: 800, letterSpacing: "-0.025em" }}
            >
              <Box component="span" sx={{ color: "#ffffff" }}>
                Extract
              </Box>
              <Box component="span" sx={{ color: "#c6602f" }}>
                Fast
              </Box>
            </Typography>
          </Button>

          {/* Navigation Links */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
            }}
          >
            {NAV_LINKS.map((link) => (
              <Button
                key={link.id}
                onClick={() => handleScroll(link)}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.4)",
                  textTransform: "none",
                  p: 0,
                  minWidth: "auto",
                  transition: "color 0.2s ease-in-out",
                  "&:hover": {
                    color: "#2a9d8f",
                    backgroundColor: "transparent",
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>

          {/* Legal Links */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            {LEGAL_LINKS.map((legal, index) => (
              <Button
                key={index}
                onClick={() => {
  window.location.href = legal.path;
}}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.3)",
                  textTransform: "none",
                  px: 1,
                  py: 0.5,
                  minWidth: "auto",
                  transition: "color 0.2s ease-in-out",
                  "&:hover": {
                    color: "#2a9d8f",
                    backgroundColor: "transparent",
                  },
                }}
              >
                {legal.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Middle Row: Social Links */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            pb: 4,
            mt: 2.5,
          }}
        >
          {socialLinks.map((link, index) => {
            const IconComponent = link.icon;
            return (
              <Link
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: "rgba(255, 255, 255, 0.4)",
                  transition: "color 0.2s ease-in-out",
                  "&:hover": {
                    color: "#2a9d8f",
                  },
                }}
              >
                {link.name === "X" ? (
                  <Box
                    component="svg"
                    sx={{ width: 20, height: 20 }}
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <mask
                      id="mask0_90_734"
                      height="24"
                      maskUnits="userSpaceOnUse"
                      style={{ maskType: "luminance" }}
                      width="24"
                      x="0"
                      y="0"
                    >
                      <path d="M0 0H24V24H0V0Z" fill="white" />
                    </mask>
                    <g mask="url(#mask0_90_734)">
                      <path
                        d="M18.9 1.125H22.5806L14.5406 10.3376L24 22.8759H16.5943L10.7897 15.273L4.15543 22.8759H0.471429L9.07029 13.0187L0 1.12671H7.59429L12.8331 8.07471L18.9 1.125ZM17.6057 20.6679H19.6457L6.48 3.21814H4.29257L17.6057 20.6679Z"
                        fill="currentColor"
                      />
                    </g>
                  </Box>
                ) : (
                  <IconComponent sx={{ fontSize: 20 }} />
                )}
              </Link>
            );
          })}
        </Box>

{/* Bottom Row: Copyrights & Developer info */}
<Box sx={{ pt: 3.5, textAlign: "center" }}>
  <Typography
    variant="body2"
    sx={{
      fontSize: "0.75rem",
      color: "rgba(255, 255, 255, 0.28)",
      lineHeight: 1.5,
    }}
  >
    Developed by{" "}
    <Link
      href="https://powerplatformpros.com.au"
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{
        color: "rgba(255, 255, 255, 0.5)",
        textDecorationColor: "rgba(255, 255, 255, 0.5)",
        "&:hover": {
          color: "rgba(255, 255, 255, 0.75)",
          textDecorationColor: "rgba(255, 255, 255, 0.75)",
        },
      }}
    >
      Power Platform Pros Pty Ltd
    </Link>
    . Secure and business-ready automation.
  </Typography>
</Box>
      </Container>
    </Box>
  );
}